import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useAuth } from "@/hooks/useAuth";

// Escape hatch for the pre-verification / pre-onboarding dead-end screens
// (CheckEmail, Onboarding). Without this a freshly registered account that
// never verifies is trapped with no way to sign out or delete itself, which
// fails App Store guideline 5.1.1(v) if a reviewer registers instead of using
// the demo account. Deletion hits the same endpoint as the account page:
// password accounts re-enter their password, OAuth accounts confirm without one.
export default function UnverifiedAccountActions() {
  const { user } = useAuth();
  // The auth-user cache can hold a partial object (from register/onboarding
  // setQueryData) that omits hasPassword, and staleTime: Infinity keeps it, so
  // refetch the authoritative record so the password field shows for password
  // accounts (trap #1).
  useEffect(() => {
    void queryClient.refetchQueries({ queryKey: ["/api/auth/user"] });
  }, []);
  const hasPassword = Boolean((user as any)?.hasPassword);

  const [confirming, setConfirming] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const signOut = async () => {
    try {
      await apiRequest("POST", "/api/logout", {});
    } catch {
      /* clearing local state and reloading is what actually matters */
    }
    queryClient.clear();
    window.location.replace("/");
  };

  const deleteAccount = async () => {
    setError(null);
    if (confirmText.trim().toUpperCase() !== "DELETE") {
      setError("Type DELETE to confirm.");
      return;
    }
    if (hasPassword && !password) {
      setError("Enter your password to confirm.");
      return;
    }
    setBusy(true);
    try {
      await apiRequest("POST", "/api/account/delete", {
        confirm: true,
        ...(hasPassword ? { password } : {}),
      });
      queryClient.clear();
      window.location.replace("/");
    } catch (err) {
      setBusy(false);
      setError(err instanceof Error ? err.message : "Couldn't delete the account.");
    }
  };

  return (
    <div className="w-full max-w-md space-y-3 text-center">
      {!confirming ? (
        <div className="flex items-center justify-center gap-4 text-sm">
          <button type="button" onClick={signOut} className="text-muted-foreground underline">
            Sign out
          </button>
          <button
            type="button"
            onClick={() => setConfirming(true)}
            className="text-destructive underline"
          >
            Delete this account
          </button>
        </div>
      ) : (
        <div className="space-y-2 rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-left">
          <p className="text-sm text-muted-foreground">
            This permanently deletes the account you just created. This can't be undone.
          </p>
          {hasPassword && (
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Your password"
            />
          )}
          <Input
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="Type DELETE to confirm"
          />
          {error && <p className="text-sm text-destructive">{error}</p>}
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setConfirming(false);
                setError(null);
              }}
            >
              Cancel
            </Button>
            <Button type="button" variant="destructive" disabled={busy} onClick={deleteAccount}>
              {busy ? "Deleting…" : "Delete account"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
